import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, Atom } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";

const navItems = [
    { to: "/", label: "Home" },
    { to: "/products", label: "Catalog" },
    { to: "/research", label: "Research" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const { totalCount, setIsOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <header
            className="sticky top-0 z-50 glass border-b border-white/10"
            data-testid="main-navbar"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-[0_0_18px_rgba(99,102,241,0.45)] transition-transform duration-300 group-hover:scale-105">
                        <Atom className="w-4 h-4 text-white" strokeWidth={2.2} />
                    </span>
                    <span className="font-light tracking-[-0.02em] text-lg">
                        Quantum<span className="text-indigo-400 font-medium"> Genesis Labs</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-4 py-2 text-sm rounded-full transition-colors ${
                                    isActive
                                        ? "text-white bg-white/5"
                                        : "text-slate-400 hover:text-white"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsOpen(true)}
                        className="relative p-2.5 rounded-full hover:bg-white/5 transition-colors"
                        data-testid="cart-icon-button"
                        aria-label="Open cart"
                    >
                        <ShoppingBag className="w-5 h-5 text-slate-200" />
                        {totalCount > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-500 text-[10px] font-medium text-white flex items-center justify-center"
                                data-testid="cart-count-badge"
                            >
                                {totalCount}
                            </span>
                        )}
                    </button>

                    <button
                        className="md:hidden p-2.5 rounded-full hover:bg-white/5"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="md:hidden border-t border-white/10 px-5 py-4 flex flex-col gap-1 bg-[#0A0A12]/95 backdrop-blur-xl">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            onClick={() => setMobileOpen(false)}
                            data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-3 py-2.5 text-sm rounded-lg ${
                                    isActive ? "text-white bg-white/5" : "text-slate-400"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </header>
    );
};

export default Navbar;
